// frontend/src/components/production-sequence/SequenceTableAvp.tsx
import { useState, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { exportSecuenciaExcel } from '@/utils/exportSecuenciaExcel';
import { TableToolbar } from '@/components/common/TableToolbar';
import { Loader } from '@/components/common/Loader';
import { Pagination } from '@/components/common/Pagination';
import { SortArrow } from '@/components/common/SortableHeader';
import { AggregationMenu } from '@/components/common/AggregationMenu';
import { TableTotalsFooter } from '@/components/common/TableTotalsFooter';
import { useColumnAggregations } from '@/hooks/useColumnAggregations';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { fmtNum, fmtDate } from './sequenceFormat';
import type { SortField, ProductionOrder } from '@/types/productionSequence';
import type { AggColumn } from '@/types';
import type { UseProductionSequenceReturn } from '@/hooks/useProductionSequence';
import { useScrollToCurrentOrder } from '@/hooks/useScrollToCurrentOrder';
import { BarraMarcas, BotonMarca, CabeceraMarca, useMarcadas, useMarcasFilas } from '@/components/common/MarcasFilas';
import { claveFila } from '@/utils/marcasFilas';

const PAGE_SIZE = 200;

type Kind = 'text' | 'number';
interface AvpCol { field: SortField; key: string; kind: Kind; align?: 'right'; }

// Las 8 columnas visibles para el rol AVP, en orden.
const AVP_COLUMNS: AvpCol[] = [
  { field: 'id',                 key: 'ot',          kind: 'text' },
  { field: 'referencia',         key: 'referencia',  kind: 'text' },
  { field: 'descripcion',        key: 'descripcion', kind: 'text' },
  { field: 'udOT',               key: 'udOT',        kind: 'number', align: 'right' },
  { field: 'udProducidas',       key: 'udProd',      kind: 'number', align: 'right' },
  { field: 'udPendientes',       key: 'udPte',       kind: 'number', align: 'right' },
  { field: 'fechaPlanificadaFin',key: 'fPlanFin',    kind: 'text' },
  { field: 'fechaRealFin',       key: 'fRealFin',    kind: 'text' },
];

// Las tres columnas de unidades salen sumadas de fábrica.
const AGG_COLS: AggColumn<ProductionOrder>[] = AVP_COLUMNS
  .filter(c => c.kind === 'number')
  .map(c => ({ key: c.key, numeric: true, valueKey: c.field, defaultAgg: 'sum' }));

const AGG_KEYS = new Set(AGG_COLS.map(c => c.key));

function cellContent(o: ProductionOrder, field: SortField): ReactNode {
  switch (field) {
    case 'id':                  return o.id;
    case 'referencia':          return o.referencia;
    case 'descripcion':         return o.descripcion;
    case 'udOT':                return fmtNum(o.udOT);
    case 'udProducidas':        return fmtNum(o.udProducidas);
    case 'udPendientes':        return fmtNum(o.udPendientes);
    case 'fechaPlanificadaFin': return fmtDate(o.fechaPlanificadaFin);
    case 'fechaRealFin':        return fmtDate(o.fechaRealFin);
    default:                    return null;
  }
}

export function SequenceTableAvp({ seq, dark, header }: {
  seq: UseProductionSequenceReturn; dark: boolean; header?: ReactNode;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [openFilter, setOpenFilter] = useState<SortField | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<DOMRect | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Mismo ámbito que la tabla completa: son las mismas OTs, sólo que con menos
  // columnas. Un usuario ve una u otra según su rol, nunca las dos a la vez.
  const marcas = useMarcasFilas('secuencia');
  // Las filas se pintan dentro de un `.map`, así que no cabe un hook por fila:
  // se lee el conjunto entero y se pasa `marcada` a cada botón. La página es de
  // 200 filas como mucho.
  const marcadas = useMarcadas(marcas);
  const marcadoCls = dark ? 'fila-marcada-dark' : 'fila-marcada';

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500';
  const thBase = `px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider select-none whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`;
  const tdBase = `px-3 py-2 text-[12px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`;
  const theadBg = dark ? 'bg-slate-800' : 'bg-slate-50';
  const surfaceBg = dark ? 'bg-slate-900' : 'bg-white';
  const btnNeutral = dark
    ? 'text-[11px] px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 transition-colors font-medium cursor-pointer'
    : 'text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm transition-colors font-medium cursor-pointer';

  const rows = seq.visibleOrders;
  const agg = useColumnAggregations({ storageKey: 'secuenciaAvp', rows, columns: AGG_COLS });
  useScrollToCurrentOrder({ orders: rows, isLoading: seq.isLoading, scrollRef, pageSize: PAGE_SIZE, page, setPage, resetKey: seq.selectedLine });
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function exportExcel() {
    void exportSecuenciaExcel(seq.orders, t, t('sequence.excel.filename'), true);
  }

  const table = (
    <table className={`w-full border-collapse text-left ${surfaceBg}`}>
      <thead>
        <tr>
          <CabeceraMarca className={`${theadBg} sticky top-0 z-20`} />
          {AVP_COLUMNS.map(c => {
            const filter = seq.columnFilters.find(f => f.field === c.field);
            const sortDir = seq.activeSort?.field === c.field ? seq.activeSort.direction : null;
            return (
              <th key={c.key} className={`group ${thBase} ${theadBg} relative ${c.align === 'right' ? 'text-right' : ''}`}
                  style={{ position: 'sticky', top: 0, zIndex: 20 }}>
                <span className="inline-flex items-center gap-1">
                  {t(`sequence.col.${c.key}`)}
                  <SortArrow active={!!sortDir} dir={sortDir ?? 'asc'} dark={dark} />
                  {AGG_KEYS.has(c.key) && (
                    <AggregationMenu
                      dark={dark}
                      colLabel={t(`sequence.col.${c.key}`)}
                      active={agg.aggState[c.key] ?? null}
                      onChange={fn => agg.setAgg(c.key, fn)}
                    />
                  )}
                  <button
                    onClick={e => {
                      const next = openFilter === c.field ? null : c.field;
                      setOpenFilter(next);
                      setFilterAnchor(next ? e.currentTarget.getBoundingClientRect() : null);
                    }}
                    className={`ml-0.5 cursor-pointer ${filter ? 'text-sky-500' : dark ? 'text-slate-600 hover:text-slate-300' : 'text-slate-300 hover:text-slate-600'}`}
                    aria-label="filter"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 2h10L7 7v3L5 11V7L1 2z"/></svg>
                  </button>
                </span>
                {openFilter === c.field && (
                  <ColumnFilterPopover
                    dark={dark}
                    colLabel={t(`sequence.col.${c.key}`)}
                    kind={c.kind}
                    currentValue={filter?.value ?? ''}
                    options={[]}
                    sortDir={sortDir}
                    anchorRect={filterAnchor}
                    onApply={v => seq.setColumnFilter(c.field, c.kind, v)}
                    onClear={() => seq.clearColumnFilter(c.field)}
                    onSort={dir => seq.setColumnSort(c.field, dir)}
                    onClose={() => setOpenFilter(null)}
                  />
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {pageRows.length === 0 ? (
          <tr>
            <td colSpan={AVP_COLUMNS.length + 1} className={`px-3 py-12 text-center text-[13px] ${mutedTxt}`}>
              {seq.isLoading
                ? <Loader />
                : t('sequence.noResultsSub')}
            </td>
          </tr>
        ) : pageRows.map(o => {
          // Clave de negocio: la OT. La misma que usa la tabla completa, para que
          // la marca sea la misma fila en las dos vistas.
          const clave = claveFila(o.id);
          const marcada = marcadas.has(clave);
          return (
          <tr key={o.id} data-seq-row={o.id} className={`border-t ${dark ? 'border-white/4' : 'border-slate-100'} ${marcada ? marcadoCls : ''}`}>
            <td className="w-6 px-1 py-2 align-middle">
              <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
            </td>
            {AVP_COLUMNS.map(c => (
              <td
                key={c.key}
                className={`${tdBase} ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.field === 'id' ? 'font-mono font-semibold' : ''} ${c.field === 'referencia' ? 'font-semibold' : ''} ${(c.field === 'referencia' || c.field === 'descripcion') ? 'max-w-[240px] truncate' : ''}`}
                title={c.field === 'referencia' ? o.referencia : c.field === 'descripcion' ? o.descripcion : undefined}
              >
                {cellContent(o, c.field)}
              </td>
            ))}
          </tr>
          );
        })}
      </tbody>
      {pageRows.length > 0 && (
        <TableTotalsFooter
          dark={dark}
          columns={[
            // Ocupa columna aunque no totalice: si no, el pie iría desplazado.
            { key: '__marca__' },
            ...AVP_COLUMNS.map(c => ({
              key: c.key,
              align: (c.align === 'right' ? 'right' : 'left') as 'right' | 'left',
            })),
          ]}
          results={agg.results}
          rowCount={rows.length}
        />
      )}
    </table>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="shrink-0">
        <TableToolbar
          dark={dark}
          left={<span className={`text-[11px] font-semibold ${mutedTxt}`}>{rows.length.toLocaleString('es-ES')} {t('sequence.rowsLabel')}</span>}
          right={
            <button onClick={exportExcel} className={btnNeutral} title={t('common.exportExcel')}>
              <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h10M8 3v7M5 7l3 3 3-3"/></svg>
              {t('common.exportExcel')}
            </button>
          }
        />
      </div>

      <BarraMarcas dark={dark} marcas={marcas} className="shrink-0" />

      <div className="relative flex-1 min-h-0 flex flex-col">
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto seq-scroll">
          {header && <div className="sticky left-0 px-4 pt-4 pb-4">{header}</div>}
          {table}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="shrink-0">
          <Pagination
            dark={dark} page={safePage} totalPages={totalPages} totalItems={rows.length}
            itemLabel={t('sequence.rowsLabel')} pageLabel={t('common.page')} ofLabel={t('common.of')}
            onFirst={() => setPage(1)} onPrev={() => setPage(safePage - 1)}
            onNext={() => setPage(safePage + 1)} onLast={() => setPage(totalPages)}
          />
        </div>
      )}
    </div>
  );
}
